import React, { useState, useRef, useEffect } from "react"
import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion"
import { TrendingUp, TrendingDown, ChevronDown, Download, Star, DollarSign, BarChart3, ThumbsUp, ThumbsDown } from "lucide-react"
import { buildMetricCards } from "../../utils/aiReportMetrics"

// CountUp animation component
const CountUp = ({ target, prefix = "", suffix = "", decimals = 0, isRevenue = false }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => {
    const absValue = Math.abs(v);
    let formattedValue;

    if (absValue >= 1000000000) {
      formattedValue = (v / 1000000000).toFixed(1).replace(/\.?0+$/, '') + 'B';
    } else if (absValue >= 1000000) {
      formattedValue = (v / 1000000).toFixed(1).replace(/\.?0+$/, '') + 'M';
    } else if (absValue >= 1000) {
      formattedValue = (v / 1000).toFixed(1).replace(/\.?0+$/, '') + 'K';
    } else {
      const parts = v.toFixed(decimals).split('.');
      const integerPart = parseFloat(parts[0]).toLocaleString();
      formattedValue = parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
    }

    return `${prefix}${formattedValue}${suffix}`;
  });
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(`${prefix}0${suffix}`);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(count, target, { duration: 1.2, ease: "easeOut" });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => { controls.stop(); unsub(); };
  }, [isInView, target, count, rounded]);

  return <span ref={ref}>{display}</span>;
};

// Metric Card Component
const MetricCard = ({ label, value, change, direction, delay = 0, detail, isRange = false }) => {
  const [expanded, setExpanded] = useState(false);
  const isExpanded = expanded;

  const iconMap = {
    up: <TrendingUp className="h-4 w-4" />,
    down: <TrendingDown className="h-4 w-4" />,
  };

  const colorMap = {
    up: "text-success",
    down: "text-destructive",
    neutral: "text-muted-foreground",
  };

  const borderGlow = {
    up: "hover:shadow-[0_0_25px_hsl(var(--success)/0.2)] hover:border-success/40",
    down: "hover:shadow-[0_0_25px_hsl(var(--destructive)/0.2)] hover:border-destructive/40",
    neutral: "hover:border-muted-foreground/30",
  };

  // Custom styles for dark theme
  const cardStyle = {
    backgroundColor: "rgba(23, 24, 33, 1)",
    // backgroundColor: "hsl(230, 22%, 12%)",
    border: "1px solid #374151",
    borderRadius: "12px",
    padding: "16px",
    cursor: detail ? "pointer" : "default",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)"
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#9ca3af",
    marginBottom: "6px"
  };

  // One size for every card (range or single number) so they read as a
  // uniform grid — sized to fit the longest range ("$1.71K – $3.55K") on one
  // line; nowrap + ellipsis is just a safety net if a value runs longer still.
  const valueStyle = {
    fontSize: "20px",
    fontWeight: "bold",
    color: "#ffffff",
    fontFamily: "monospace",
    marginBottom: "6px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  };

  const changeStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "500",
    ...colorMap[direction] === "text-success" && { color: "#10b981" },
    ...colorMap[direction] === "text-destructive" && { color: "#ef4444" },
    ...colorMap[direction] === "text-muted-foreground" && { color: "#6b7280" }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay }}
      whileHover={{
        scale: 1.03,
        transition: { duration: 0.2 },
        boxShadow: direction === "up" ? "0 0 25px rgba(16, 185, 129, 0.4)" :
          direction === "down" ? "0 0 25px rgba(239, 68, 68, 0.4)" :
            "0 0 15px rgba(156, 163, 175, 0.2)",
        borderColor: direction === "up" ? "#10b981" :
          direction === "down" ? "#ef4444" :
            "#9ca3af"
      }}
      onClick={() => detail && setExpanded(!expanded)}
      className={`metric-card ${borderGlow[direction]}`}
      style={cardStyle}
    >
      <p style={labelStyle}>
        {label}
      </p>
      <p style={valueStyle}>
        {isRange ? (
          value
        ) : (
          <CountUp
            target={parseFloat(value.replace(/[^0-9.-]/g, '')) || 0}
            prefix={value.match(/^\$|^\D+/)?.[0] || ""}
            suffix={value.match(/\D+$/)?.[0] || ""}
            decimals={value.includes('.') ? value.split('.')[1].replace(/[^0-9]/g, '').length : 0}
          />
        )}
      </p>
      <div style={changeStyle}>
        {direction !== "neutral" && iconMap[direction]}
        <span>{change}</span>
        {detail && (
          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} style={{ marginLeft: "auto" }}>
            <ChevronDown className="h-3.5 w-3.5" style={{ color: "#6b7280" }} />
          </motion.div>
        )}
      </div>
      <motion.div
        initial={false}
        animate={{ height: isExpanded ? "auto" : 0, opacity: isExpanded ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        style={{ overflow: "hidden" }}
      >
        {detail && (
          <p style={{
            fontSize: "12px",
            color: "#9ca3af",
            marginTop: "12px",
            paddingTop: "12px",
            borderTop: "1px solid #374151"
          }}>
            {detail}
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};

// AI Report Display Component
const AIReportDisplay = ({ reportData, appName, onGoBack }) => {
  const [expandedObservation, setExpandedObservation] = React.useState(null)
  const [activeRecommendationTab, setActiveRecommendationTab] = React.useState(0)
  const [expandedThemes, setExpandedThemes] = React.useState({})
  const [sentimentHoverIndex, setSentimentHoverIndex] = React.useState(null)
  const [sentimentTooltip, setSentimentTooltip] = React.useState(null)

  // Toggle theme expansion (accordion behavior)
  const toggleThemeExpansion = (themeKey) => {
    setExpandedThemes(prev => {
      // If clicking on already expanded theme, close it
      if (prev[themeKey]) {
        return {}
      }
      // Otherwise, close all others and expand this one
      return { [themeKey]: true }
    })
  }

  // Parse the report data (structured JSON returned by the AI report API)
  const parseReportData = (data) => {
    const empty = {
      title: '',
      analysisPeriod: '',
      reportGenerated: '',
      metrics: [],
      userSentiment: null,
      keyObservations: [],
      metricAnomalies: [],
      recommendations: []
    }

    if (!data || typeof data !== 'object') return empty

    const sentiment = data.sentiment
    const userSentiment = sentiment ? {
      description: sentiment.overall_summary || '',
      positive: sentiment.breakdown?.positive_percent ?? 0,
      negative: sentiment.breakdown?.negative_percent ?? 0,
      neutral: sentiment.breakdown?.neutral_percent ?? 0,
      positiveThemes: (sentiment.top_positive_themes || []).map(t => ({ name: t.theme, examples: t.examples || [] })),
      negativeThemes: (sentiment.top_negative_themes || []).map(t => ({ name: t.issue, examples: t.examples || [] })),
      bugComplaints: (sentiment.bug_complaints || []).map(b => ({ name: b.bug, examples: b.examples || [] })),
    } : null

    const keyObservations = (data.key_observations || []).map(obs => ({
      title: obs.title || '',
      wellPerforming: obs.well_performing_keywords || [],
      underperforming: obs.underperforming_keywords || [],
      correlation: obs.correlation || '',
      explanation: obs.explanation || '',
    }))

    const metricAnomalies = (data.anomalies || []).map(anomaly => ({
      metric: anomaly.metric || anomaly.title || 'Metric',
      date: anomaly.date || anomaly.detected_on || '',
      changePercent: anomaly.change_percent || anomaly.changePercent || anomaly.description || '',
    }))

    const rec = data.recommendations || {}
    const recommendations = [
      {
        title: 'Keyword Expansion',
        items: (rec.keyword_expansion || []).map(k => `${k.keyword} (${k.label})`)
      },
      {
        title: 'Bug Fix Plan',
        items: rec.bug_fix_plan || []
      },
      {
        title: 'Keyword Tracking & Monitoring',
        items: rec.keyword_tracking_monitoring || []
      }
    ].filter(tab => tab.items.length > 0)

    return {
      title: data.app_name || '',
      analysisPeriod: data.analysis_period || '',
      reportGenerated: data.report_generated || '',
      metrics: buildMetricCards(data.metrics),
      userSentiment,
      keyObservations,
      metricAnomalies,
      recommendations
    }
  }

  const parsed = parseReportData(reportData)

  return (
    <div style={{ color: '#ffffff' }}>
      {/* Metrics Grid */}
      <div className="row mb-4">
        {parsed.metrics.map((metric, index) => (
          <div key={index} className="col-md-3 col-lg-3 mb-3">
            <MetricCard
              label={metric.label}
              value={metric.value}
              change={metric.change}
              direction={metric.direction}
              delay={index * 0.1}
              detail={metric.detail}
              isRange={metric.isRange}
            />
          </div>
        ))}
      </div>

      {/* User Sentiment Section */}
      {parsed.userSentiment && (
        <>
          <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>User Sentiment</h3>

          {/* Sentiment Overview */}
          <div className="row mb-4">
            {/* DONUT CHART */}
            <div className="col-md-4">
              <div style={{
                backgroundColor: "rgba(23, 24, 33, 1)",
                // backgroundColor: "hsl(230, 22%, 12%)",
                border: '1px solid #374151',
                borderRadius: '8px',
                padding: '16px'
              }}>
                {/* DONUT + LEGEND */}
                <div className="d-flex align-items-center gap-3">
                  {/* DONUT */}
                  <div
                    style={{
                      width: 150,
                      height: 150,
                      borderRadius: "50%",
                      background: sentimentHoverIndex === 0
                        ? `conic-gradient(
                            #4ade80 0deg ${parsed.userSentiment.positive * 3.6}deg,
                            #b8860b ${parsed.userSentiment.positive * 3.6}deg ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg,
                            #8b2635 ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg 360deg
                          )`
                        : sentimentHoverIndex === 1
                          ? `conic-gradient(
                            #2d5a3d 0deg ${parsed.userSentiment.positive * 3.6}deg,
                            #fbbf24 ${parsed.userSentiment.positive * 3.6}deg ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg,
                            #8b2635 ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg 360deg
                          )`
                          : sentimentHoverIndex === 2
                            ? `conic-gradient(
                            #2d5a3d 0deg ${parsed.userSentiment.positive * 3.6}deg,
                            #b8860b ${parsed.userSentiment.positive * 3.6}deg ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg,
                            #ef4444 ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg 360deg
                          )`
                            : `conic-gradient(
                            #2d5a3d 0deg ${parsed.userSentiment.positive * 3.6}deg,
                            #b8860b ${parsed.userSentiment.positive * 3.6}deg ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg,
                            #8b2635 ${(parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6}deg 360deg
                          )`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      minWidth: 150,
                      minHeight: 150,
                      position: "relative",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: sentimentHoverIndex !== null ? "0 0 12px rgba(246, 37, 90, 0.15)" : "none"
                    }}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const centerX = rect.left + rect.width / 2;
                      const centerY = rect.top + rect.height / 2;
                      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI) + 90;
                      const normalizedAngle = angle < 0 ? angle + 360 : angle;

                      let index = -1;
                      if (normalizedAngle < parsed.userSentiment.positive * 3.6) {
                        index = 0;
                        setSentimentTooltip({ name: "Positive", value: parsed.userSentiment.positive, color: "#4ade80" });
                      } else if (normalizedAngle < (parsed.userSentiment.positive + (parsed.userSentiment.neutral || 0)) * 3.6) {
                        index = 1;
                        setSentimentTooltip({ name: "Neutral", value: parsed.userSentiment.neutral || 0, color: "#fbbf24" });
                      } else {
                        index = 2;
                        setSentimentTooltip({ name: "Negative", value: parsed.userSentiment.negative, color: "#ef4444" });
                      }
                      setSentimentHoverIndex(index);
                    }}
                    onMouseLeave={() => {
                      setSentimentHoverIndex(null);
                      setSentimentTooltip(null);
                    }}
                  >
                    <div
                      style={{
                        width: 95,
                        height: 95,
                        borderRadius: "50%",
                        background: "#1c1f2e",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <h4 className="text-white mb-0" style={{ fontSize: "12px", textAlign: "center" }}>
                        {parsed.userSentiment.positive}
                      </h4>
                      <h4 className="text-white mb-0" style={{ fontSize: "12px", textAlign: "center" }}>
                        %
                      </h4>
                    </div>

                    {/* Tooltip */}
                    {sentimentTooltip && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -10 }}
                        transition={{ duration: 0.2 }}
                        style={{
                          position: "absolute",
                          top: "-55px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor: "rgba(23, 24, 33, 0.98)",
                          border: `2px solid ${sentimentTooltip.color}`,
                          borderRadius: "8px",
                          padding: "10px 14px",
                          whiteSpace: "nowrap",
                          zIndex: 1000,
                          boxShadow: `0 4px 12px ${sentimentTooltip.color}25`
                        }}
                      >
                        <div style={{ fontSize: "12px", fontWeight: "600", color: sentimentTooltip.color, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          {sentimentTooltip.name}
                        </div>
                        <div style={{ fontSize: "18px", fontWeight: "bold", color: sentimentTooltip.color }}>
                          {sentimentTooltip.value}%
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* LEGEND */}
                  <div className="d-flex flex-column gap-2">
                    <motion.div
                      className="d-flex align-items-center gap-2"
                      style={{
                        cursor: 'pointer',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        backgroundColor: sentimentHoverIndex === 0 ? 'rgba(74, 222, 128, 0.1)' : 'transparent',
                        border: sentimentHoverIndex === 0 ? '1px solid rgba(74, 222, 128, 0.5)' : '1px solid transparent',
                        opacity: sentimentHoverIndex === null || sentimentHoverIndex === 0 ? 1 : 0.4,
                        transition: 'all 0.2s ease',
                        transform: sentimentHoverIndex === 0 ? 'scale(1.05)' : 'scale(1)'
                      }}
                      onMouseEnter={() => {
                        setSentimentHoverIndex(0);
                        setSentimentTooltip({ name: "Positive", value: parsed.userSentiment.positive, color: "#4ade80" });
                      }}
                      onMouseLeave={() => {
                        setSentimentHoverIndex(null);
                        setSentimentTooltip(null);
                      }}
                    >
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          backgroundColor: sentimentHoverIndex === 0 ? "#4ade80" : "#2d5a3d",
                          borderRadius: 3,
                          transition: 'all 0.2s ease',
                          boxShadow: sentimentHoverIndex === 0 ? '0 0 4px rgba(74, 222, 128, 0.3)' : 'none'
                        }}
                      ></div>
                      <span className="text-white small" style={{ fontWeight: sentimentHoverIndex === 0 ? '600' : '400', transition: 'all 0.2s ease' }}>
                        Positive {parsed.userSentiment.positive}%
                      </span>
                    </motion.div>
                    {parsed.userSentiment.neutral > 0 && (
                      <motion.div
                        className="d-flex align-items-center gap-2"
                        style={{
                          cursor: 'pointer',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          backgroundColor: sentimentHoverIndex === 1 ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
                          border: sentimentHoverIndex === 1 ? '1px solid rgba(251, 191, 36, 0.5)' : '1px solid transparent',
                          opacity: sentimentHoverIndex === null || sentimentHoverIndex === 1 ? 1 : 0.4,
                          transition: 'all 0.2s ease',
                          transform: sentimentHoverIndex === 1 ? 'scale(1.05)' : 'scale(1)'
                        }}
                        onMouseEnter={() => {
                          setSentimentHoverIndex(1);
                          setSentimentTooltip({ name: "Neutral", value: parsed.userSentiment.neutral || 0, color: "#fbbf24" });
                        }}
                        onMouseLeave={() => {
                          setSentimentHoverIndex(null);
                          setSentimentTooltip(null);
                        }}
                      >
                        <div
                          style={{
                            width: 14,
                            height: 14,
                            backgroundColor: sentimentHoverIndex === 1 ? "#fbbf24" : "#b8860b",
                            borderRadius: 3,
                            transition: 'all 0.2s ease',
                            boxShadow: sentimentHoverIndex === 1 ? '0 0 4px rgba(251, 191, 36, 0.3)' : 'none'
                          }}
                        ></div>
                        <span className="text-white small" style={{ fontWeight: sentimentHoverIndex === 1 ? '600' : '400', transition: 'all 0.2s ease' }}>
                          Neutral {parsed.userSentiment.neutral}%
                        </span>
                      </motion.div>
                    )}
                    <motion.div
                      className="d-flex align-items-center gap-2"
                      style={{
                        cursor: 'pointer',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        backgroundColor: sentimentHoverIndex === 2 ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                        border: sentimentHoverIndex === 2 ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid transparent',
                        opacity: sentimentHoverIndex === null || sentimentHoverIndex === 2 ? 1 : 0.4,
                        transition: 'all 0.2s ease',
                        transform: sentimentHoverIndex === 2 ? 'scale(1.05)' : 'scale(1)'
                      }}
                      onMouseEnter={() => {
                        setSentimentHoverIndex(2);
                        setSentimentTooltip({ name: "Negative", value: parsed.userSentiment.negative, color: "#ef4444" });
                      }}
                      onMouseLeave={() => {
                        setSentimentHoverIndex(null);
                        setSentimentTooltip(null);
                      }}
                    >
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          backgroundColor: sentimentHoverIndex === 2 ? "#ef4444" : "#8b2635",
                          borderRadius: 3,
                          transition: 'all 0.2s ease',
                          boxShadow: sentimentHoverIndex === 2 ? '0 0 4px rgba(239, 68, 68, 0.3)' : 'none'
                        }}
                      ></div>
                      <span className="text-white small" style={{ fontWeight: sentimentHoverIndex === 2 ? '600' : '400', transition: 'all 0.2s ease' }}>
                        Negative {parsed.userSentiment.negative}%
                      </span>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>

            {/* POSITIVE THEMES */}
            <div className="col-md-4">
              <div style={{
                backgroundColor: "rgba(23, 24, 33, 1)",
                // backgroundColor: "hsl(230, 22%, 12%)",
                border: '1px solid #374151',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <h4 style={{ fontSize: '18px', marginBottom: '12px', color: '#9ca3af' }}>
                  <ThumbsUp style={{ height: '18px', width: '18px', color: '#10b981', marginRight: '6px' }} />
                  Top Positive Themes
                </h4>
                {parsed.userSentiment.positiveThemes.map((theme, index) => {
                  const themeKey = `positive-${index}`
                  const isExpanded = expandedThemes[themeKey]

                  return (
                    <motion.div
                      key={index}
                      style={{ marginBottom: '12px' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div
                        onClick={() => toggleThemeExpansion(themeKey)}
                        style={{
                          marginBottom: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: isExpanded ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                          border: isExpanded ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid transparent',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isExpanded ? 'rgba(16, 185, 129, 0.1)' : 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <motion.div
                            animate={{
                              height: isExpanded ? '16px' : '4px',
                              backgroundColor: '#10b981'
                            }}
                            transition={{ duration: 0.3 }}
                            style={{
                              width: '2px',
                              height: '4px',
                              backgroundColor: '#10b981',
                              flexShrink: 0
                            }}
                          />
                          <span style={{ color: '#ffffff', fontWeight: '500' }}>{theme.name}</span>
                        </div>
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="h-3.5 w-3.5" style={{ color: '#6b7280' }} />
                        </motion.div>
                      </div>
                      <motion.div
                        initial={false}
                        animate={{
                          height: isExpanded ? 'auto' : 0,
                          opacity: isExpanded ? 1 : 0
                        }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden', marginTop: '8px' }}
                      >
                        <div style={{ paddingLeft: '20px' }}>
                          {theme.examples.map((example, idx) => (
                            <div key={idx} style={{
                              fontSize: '12px',
                              color: '#9ca3af',
                              fontStyle: 'italic',
                              marginBottom: '4px',
                              padding: '2px 0'
                            }}>
                              "{example}"
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    </motion.div>
                  )
                })}
              </div>
            </div>

            {/* NEGATIVE THEMES */}
            <div className="col-md-4">
              <div style={{
                backgroundColor: "rgba(23, 24, 33, 1)",
                // backgroundColor: "hsl(230, 22%, 12%)",
                border: '1px solid #374151',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <h4 style={{ fontSize: '18px', marginBottom: '12px', color: '#9ca3af' }}>
                  <ThumbsDown style={{ height: '18px', width: '18px', color: '#ef4444', marginRight: '6px' }} />
                  Areas for Improvement
                </h4>
                {parsed.userSentiment.negativeThemes.map((theme, index) => {
                  const themeKey = `negative-${index}`
                  const isExpanded = expandedThemes[themeKey]

                  return (
                    <motion.div
                      key={index}
                      style={{ marginBottom: '12px' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div
                        onClick={() => toggleThemeExpansion(themeKey)}
                        style={{
                          marginBottom: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: isExpanded ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                          border: isExpanded ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid transparent',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isExpanded ? 'rgba(239, 68, 68, 0.1)' : 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <motion.div
                            animate={{
                              height: isExpanded ? '16px' : '4px',
                              backgroundColor: '#ef4444'
                            }}
                            transition={{ duration: 0.3 }}
                            style={{
                              width: '2px',
                              height: '4px',
                              backgroundColor: '#ef4444',
                              flexShrink: 0
                            }}
                          />
                          <span style={{ color: '#ffffff', fontWeight: '500' }}>{theme.name}</span>
                        </div>
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="h-3.5 w-3.5" style={{ color: '#6b7280' }} />
                        </motion.div>
                      </div>
                      <motion.div
                        initial={false}
                        animate={{
                          height: isExpanded ? 'auto' : 0,
                          opacity: isExpanded ? 1 : 0
                        }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden', marginTop: '8px' }}
                      >
                        <div style={{ paddingLeft: '20px' }}>
                          {theme.examples.map((example, idx) => (
                            <div key={idx} style={{
                              fontSize: '12px',
                              color: '#9ca3af',
                              fontStyle: 'italic',
                              marginBottom: '4px',
                              padding: '2px 0'
                            }}>
                              "{example}"
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Key Observations */}
      {parsed.keyObservations.length > 0 && (
        <>
          <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>Key Observations</h3>
          <div className="row mb-4">
            {parsed.keyObservations.map((observation, index) => {
              // Determine sentiment based on actual data and explanation content
              const hasWellPerforming = observation.wellPerforming && observation.wellPerforming.length > 0;
              const hasUnderperforming = observation.underperforming && observation.underperforming.length > 0;

              // Analyze explanation for positive/negative indicators
              const explanation = observation.explanation.toLowerCase();
              const positiveWords = ['increased', 'improved', 'growth', 'up', 'higher', 'better', 'gained', 'rose'];
              const negativeWords = ['decreased', 'declined', 'down', 'lower', 'worse', 'lost', 'fell', 'dropped'];

              const hasPositiveWords = positiveWords.some(word => explanation.includes(word));
              const hasNegativeWords = negativeWords.some(word => explanation.includes(word));

              // Special case: prioritize main observation subject over comparison data
              let isPositive = false;
              let isNegative = false;

              if (observation.title.toLowerCase().includes('download')) {
                // Download observations are positive if they mention increase, even if comparing to others
                isPositive = explanation.includes('increased') || explanation.includes('growth');
              } else if (observation.title.toLowerCase().includes('revenue')) {
                // Revenue observations are negative if they mention decrease
                isNegative = explanation.includes('decreased') || explanation.includes('declined');
              } else if (observation.title.toLowerCase().includes('asr')) {
                // ASR observations are positive if they mention increase/improvement
                isPositive = explanation.includes('increased') || explanation.includes('improved') || explanation.includes('higher');
              } else if (observation.title.toLowerCase().includes('review')) {
                // Review observations are generally positive
                isPositive = true;
              } else if (hasPositiveWords && !hasNegativeWords) {
                isPositive = true;
              } else if (hasNegativeWords && !hasPositiveWords) {
                isNegative = true;
              } else {
                // Mixed signals - fall back to keyword analysis
                isPositive = hasWellPerforming && !hasUnderperforming;
                isNegative = hasUnderperforming && !hasWellPerforming;
              }

              return (
                <div key={index} className="col-md-3 mb-3">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{
                      scale: 1.03,
                      transition: { duration: 0.2, ease: "easeOut" }
                    }}
                    style={{
                      backgroundColor: isPositive ? "rgba(16, 185, 129, 0.05)" :
                        isNegative ? "rgba(239, 68, 68, 0.05)" :
                          "rgba(23, 24, 33, 1)",
                      border: isPositive ? '1px solid rgba(16, 185, 129, 0.3)' :
                        isNegative ? '1px solid rgba(239, 68, 68, 0.3)' :
                          '1px solid #374151',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: expandedObservation === index
                        ? (isPositive ? "0 0 16px rgba(16, 185, 129, 0.2)" : isNegative ? "0 0 16px rgba(239, 68, 68, 0.2)" : "0 0 16px rgba(156, 163, 175, 0.1)")
                        : "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)"
                    }}
                    onClick={() => setExpandedObservation(expandedObservation === index ? null : index)}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                      {/* Icon Container */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: isPositive ? 'rgba(16, 185, 129, 0.15)' :
                          isNegative ? 'rgba(239, 68, 68, 0.15)' :
                            'rgba(107, 114, 128, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '14px',
                        transition: 'all 0.3s ease'
                      }}>
                        {(() => {
                          const iconColor = isPositive ? '#10b981' : isNegative ? '#ef4444' : '#9ca3af';
                          if (observation.title.includes('ASR')) {
                            return <TrendingUp style={{ color: iconColor }} size={20} />;
                          } else if (observation.title.includes('Download')) {
                            return <Download style={{ color: iconColor }} size={20} />;
                          } else if (observation.title.includes('Review')) {
                            return <Star style={{ color: iconColor }} size={20} />;
                          } else if (observation.title.includes('Revenue')) {
                            return <DollarSign style={{ color: iconColor }} size={20} />;
                          } else {
                            return <BarChart3 style={{ color: iconColor }} size={20} />;
                          }
                        })()}
                      </div>

                      {/* Title */}
                      <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '8px', color: '#ffffff', lineHeight: '1.3' }}>
                        {observation.title}
                      </div>

                      {/* Description */}
                      <div style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.4', flex: 1 }}>
                        {observation.explanation.substring(0, 70)}...
                      </div>

                      {/* Sentiment Badge */}
                      <div style={{ marginTop: '12px', width: '100%' }}>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: isPositive ? '#10b981' : isNegative ? '#ef4444' : '#9ca3af',
                          backgroundColor: isPositive ? 'rgba(16, 185, 129, 0.1)' :
                            isNegative ? 'rgba(239, 68, 68, 0.1)' :
                              'rgba(107, 114, 128, 0.1)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          {isPositive ? '↑ Positive' : isNegative ? '↓ Negative' : '→ Neutral'}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>

          {/* Expanded Observation Details */}
          {expandedObservation !== null && parsed.keyObservations[expandedObservation] && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                backgroundColor: "rgba(23, 24, 33, 1)",
                // backgroundColor: "hsl(230, 22%, 12%)",
                border: '1px solid #374151',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '20px'
              }}
            >
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                {parsed.keyObservations[expandedObservation].title}
              </h4>

              <div className="row mb-3">
                <div className="col-md-6">
                  <h5 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#10b981' }}>
                    Performing Well
                  </h5>
                  {parsed.keyObservations[expandedObservation].wellPerforming.map((keyword, idx) => (
                    <span key={idx} style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.2)',
                      color: '#10b981',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginRight: '4px',
                      marginBottom: '4px',
                      display: 'inline-block'
                    }}>
                      {keyword}
                    </span>
                  ))}
                </div>
                <div className="col-md-6">
                  <h5 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#ef4444' }}>
                    Underperforming
                  </h5>
                  {parsed.keyObservations[expandedObservation].underperforming.map((keyword, idx) => (
                    <span key={idx} style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginRight: '4px',
                      marginBottom: '4px',
                      display: 'inline-block'
                    }}>
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                <strong>Correlation:</strong> {parsed.keyObservations[expandedObservation].correlation}<br />
                <strong>Explanation:</strong> {parsed.keyObservations[expandedObservation].explanation}
              </div>
            </motion.div>
          )}
        </>
      )}

      {/* Metric Anomalies */}
      {parsed.metricAnomalies.length > 0 && (
        <>
          <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>Metric Anomalies</h3>
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#ef4444' }}>
              ⚠️ Anomalies Detected
            </div>
            {parsed.metricAnomalies.map((anomaly, index) => (
              <div key={index} style={{ fontSize: '14px', marginBottom: '4px' }}>
                {anomaly.metric}: {anomaly.changePercent}{anomaly.date ? ` on ${anomaly.date}` : ''}
              </div>
            ))}
          </div>
        </>
      )}

      {/* AI Recommendations */}
      {parsed.recommendations.length > 0 && (
        <>
          <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>AI Recommendations (30-90 Days)</h3>

          {/* Single Box Container */}
          <div style={{
            backgroundColor: "rgba(23, 24, 33, 1)",
            // backgroundColor: "hsl(230, 22%, 12%)",
            border: '1px solid #374151',
            borderRadius: '12px',
            padding: '24px'
          }}>
            {/* Tab Headers */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid #374151', paddingBottom: '12px' }}>
              {parsed.recommendations.map((rec, index) => (
                <motion.div
                  key={index}
                  onClick={() => setActiveRecommendationTab(index)}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: '8px 0',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderBottom: activeRecommendationTab === index ? '2px solid #F6255A' : '2px solid transparent'
                  }}
                >
                  <span style={{
                    color: activeRecommendationTab === index ? '#F6255A' : '#9ca3af',
                    fontWeight: activeRecommendationTab === index ? '600' : '500',
                    fontSize: '14px'
                  }}>
                    {rec.title}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Tab Content */}
            <motion.div
              key={activeRecommendationTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <h4 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#F6255A' }}>
                {parsed.recommendations[activeRecommendationTab].title}
              </h4>

              <ul style={{ margin: 0, paddingLeft: '0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {parsed.recommendations[activeRecommendationTab].items.map((item, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.25, delay: idx * 0.06 }}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      fontSize: '14px',
                      color: '#e5e7eb',
                      listStyle: 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span style={{
                      color: '#F6255A',
                      fontWeight: 'bold',
                      marginTop: '1px',
                      flexShrink: 0
                    }}>
                      •
                    </span>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </>
      )}
    </div>
  )
}

export default AIReportDisplay
